import { useState, useEffect } from "react";
import { SvgClose, SvgMapMarker } from "ui/svgs";

import { Popover } from "react-tiny-popover";

import clsx from "clsx";
import { getSiteList } from "pages/dashboard/virtualGuardProgress/service";
import { showToast } from "ui/components/common";

type SiteSelectPopoverProps = {
  selectedSites: number[];
  onSitesChange: (siteIds: number[]) => void;
  multiSelect?: boolean;
};

export default function SiteSelectPopover({
  selectedSites,
  onSitesChange,
  multiSelect = true,
}: SiteSelectPopoverProps) {
  const [sites, setSites] = useState([]);

  useEffect(() => {
    getSiteList().then((res) => {
      if (res.status !== 200) {
        showToast(1, "Failed to fetch sites. Please try again later.");
        return;
      }
      setSites(res.data.sites);
    });
  }, []);

  const [isSitePopoverOpen, setIsSitePopoverOpen] = useState(false);

  const [stagedSiteIds, setStagedSiteIds] = useState([]); // To stage changes before confirming

  const handleSiteSelection = (siteId) => {
    setStagedSiteIds((prevStagedSiteIds) =>
      prevStagedSiteIds.includes(siteId)
        ? prevStagedSiteIds.filter((id) => id !== siteId)
        : multiSelect
          ? [...prevStagedSiteIds, siteId]
          : [siteId],
    );
  };

  const handleSiteCancel = () => {
    setIsSitePopoverOpen(false); // Close popover
    setSiteSearchTerm("");
  };

  //clear cameras as well
  const handleSiteOk = () => {
    setIsSitePopoverOpen(false); // Close popover
    setSiteSearchTerm("");
    onSitesChange?.(stagedSiteIds);
  };

  // Function to clear site filters
  const clearSiteIds = () => {
    setStagedSiteIds([]);
    onSitesChange?.([]);
  };

  // Function to render the display text for site filters
  const renderSiteIdsDisplay = () => {
    const count = selectedSites?.length;
    if (count === 0) return "Site";
    if (count === 1)
      return (
        sites?.find((site) => site.id === selectedSites?.[0])?.name || "1 Site"
      );
    if (count === sites?.length) return "All Sites";
    return `${sites?.find((site) => site.id === selectedSites?.[0])?.name} (${count})`;
  };

  const [siteSearchTerm, setSiteSearchTerm] = useState("");

  const filteredSites = sites?.filter((site) =>
    site.name.toLowerCase().includes(siteSearchTerm.toLowerCase()),
  );

  return (
    <Popover
      isOpen={isSitePopoverOpen}
      positions={["top", "left", "right"]}
      onClickOutside={() => setIsSitePopoverOpen(false)}
      content={
        <div className="border rounded-xl shadow-xl bg-white min-w-44 pt-2 px-2">
          <p className="text-zinc-500 ml-2 text-sm mt-2 mb-1">Select Site</p>
          {sites?.length > 5 && (
            <div className="px-2 py-2">
              <input
                type="text"
                placeholder="Search sites..."
                className="w-full p-2 border border-gray-300  rounded-xl"
                value={siteSearchTerm}
                onChange={(e) => setSiteSearchTerm(e.target.value)}
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto">
            {filteredSites?.map((site) => (
              <button
                key={site.id}
                className={clsx(
                  "block w-full  p-2",
                  stagedSiteIds.includes(site.id)
                    ? "bg-blue-100"
                    : "hover:bg-neutral-100",
                )}
                onClick={() => handleSiteSelection(site.id)}
              >
                <p className="text-sm text-left">{site.name}</p>
              </button>
            ))}
          </div>
          <div className="flex justify-end space-x-2 p-2">
            <button
              className="px-4 py-1 rounded text-sm text-blue-700 hover:text-blue-500"
              onClick={handleSiteCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1 rounded text-sm bg-blue-600 hover:bg-blue-500 text-white"
              onClick={handleSiteOk}
            >
              OK
            </button>
          </div>
        </div>
      }
    >
      {selectedSites?.length > 0 ? (
        <div className="flex items-center rounded-xl bg-blue-100 border px-3 py-2 w-44 h-[33px] overflow-hidden">
          <SvgMapMarker width="16" className="flex-shrink-0" />
          <p className="text-sm font-semibold text-left flex-grow pr-2 max-w-[140px]  pl-2 text-nowrap overflow-hidden break-words text-ellipsis">
            {renderSiteIdsDisplay()}
          </p>
          <button
            onClick={() => {
              clearSiteIds();
            }}
          >
            <SvgClose
              width="13"
              className="fill-neutral-800 hover:fill-neutral-700"
            />
          </button>
        </div>
      ) : (
        <button
          className="flex items-center rounded-xl hover:bg-zinc-200 border border-neutral-400 px-3 py-2 min-w-[76px] h-[33px]"
          onClick={() => setIsSitePopoverOpen(!isSitePopoverOpen)}
        >
          <SvgMapMarker width="16" />
          <p className="text-sm font-semibold text-left  pl-2 text-nowrap">
            Site
          </p>
        </button>
      )}
    </Popover>
  );
}
